<template>
  <div class="p-6 dark:text-white" v-if="profile">
    <div class="flex items-center justify-between mb-6">
      <h1 class="text-2xl font-semibold flex items-center dark:text-white">
        <button
          @click="$router.push({ name: 'WiFiProfiles' })"
          class="mr-4 bg-blue-600 text-white px-4 py-1.5 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 transition duration-200 text-sm font-medium flex items-center"
        >
          <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 mr-1" viewBox="0 0 20 20" fill="currentColor">
            <path fill-rule="evenodd" d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z" clip-rule="evenodd" />
          </svg>
          Back
        </button>
        WiFi Profile: {{ profile.name }}
      </h1>
      <button
        v-if="canChangeWiFiProfile || user?.is_staff"
        @click="$router.push({ name: 'EditWiFiProfile', params: { id: profile.id }, query: { schemaName: currentSchema } })"
        class="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 text-sm"
      >
        Edit
      </button>
    </div>

    <div class="mb-4">
      <span :class="[
        'px-3 py-1 text-sm rounded-full',
        profile.pending ? 'bg-yellow-100 text-yellow-800' : 'bg-green-100 text-green-800'
      ]">
        {{ profile.pending ? 'Pending Approval' : 'Approved' }}
      </span>
    </div>

    <div class="bg-white/80 dark:bg-gray-800/70 backdrop-blur-md rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6 mb-6">
      <h2 class="text-lg font-semibold mb-4">Configuration Details</h2>
      <dl class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <div>
          <dt class="text-sm text-gray-500 dark:text-gray-400">Name</dt>
          <dd class="font-medium">{{ profile.name || 'N/A' }}</dd>
        </div>
        <div>
          <dt class="text-sm text-gray-500 dark:text-gray-400">SSID</dt>
          <dd class="font-medium">{{ profile.ssid || 'N/A' }}</dd>
        </div>
        <div>
          <dt class="text-sm text-gray-500 dark:text-gray-400">Auth Type</dt>
          <dd class="font-medium">{{ authTypeLabel }}</dd>
        </div>
        <div>
          <dt class="text-sm text-gray-500 dark:text-gray-400">Frequency Band</dt>
          <dd class="font-medium">{{ frequencyBandLabel }}</dd>
        </div>
        <div v-if="isEnterprise">
          <dt class="text-sm text-gray-500 dark:text-gray-400">EAP Method</dt>
          <dd class="font-medium">{{ profile.eap_method || 'N/A' }}</dd>
        </div>
        <div v-if="isEnterprise">
          <dt class="text-sm text-gray-500 dark:text-gray-400">Identity</dt>
          <dd class="font-medium">{{ profile.identity || 'N/A' }}</dd>
        </div>
        <div>
          <dt class="text-sm text-gray-500 dark:text-gray-400">Description</dt>
          <dd class="font-medium">{{ profile.description || 'N/A' }}</dd>
        </div>
        <div>
          <dt class="text-sm text-gray-500 dark:text-gray-400">Organization</dt>
          <dd class="font-medium">{{ profile.org_name || 'N/A' }}</dd>
        </div>
      </dl>
    </div>

    <div class="bg-white/80 dark:bg-gray-800/70 backdrop-blur-md rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6 mb-6">
      <div class="flex items-center justify-between mb-4">
        <h2 class="text-lg font-semibold">Credentials</h2>
        <button
          v-if="user?.is_superuser && !revealed"
          @click="revealSecrets"
          class="bg-gray-700 text-white px-3 py-1 rounded-md hover:bg-gray-800 text-sm"
        >
          Reveal
        </button>
      </div>
      <dl class="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div v-if="isEnterprise">
          <dt class="text-sm text-gray-500 dark:text-gray-400">Password</dt>
          <dd class="font-mono">{{ revealed ? (profile.password || 'N/A') : '****' }}</dd>
        </div>
        <div v-if="isPsk">
          <dt class="text-sm text-gray-500 dark:text-gray-400">PSK</dt>
          <dd class="font-mono">{{ revealed ? (profile.psk || 'N/A') : '****' }}</dd>
        </div>
      </dl>
    </div>

    <div class="bg-white/80 dark:bg-gray-800/70 backdrop-blur-md rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
      <div class="flex items-center justify-between mb-4">
        <h2 class="text-lg font-semibold">Assigned Probes</h2>
      </div>

      <div v-if="assignmentsLoading" class="text-gray-500">Loading assignments...</div>

      <table v-else-if="assignments.length" class="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
        <thead>
          <tr>
            <th class="px-4 py-2 text-left text-xs font-medium uppercase text-gray-500 dark:text-gray-400">Probe Name</th>
            <th class="px-4 py-2 text-left text-xs font-medium uppercase text-gray-500 dark:text-gray-400">Is Active</th>
            <th class="px-4 py-2 text-left text-xs font-medium uppercase text-gray-500 dark:text-gray-400">Actions</th>
          </tr>
        </thead>
        <tbody class="divide-y divide-gray-200 dark:divide-gray-700">
          <tr v-for="assignment in assignments" :key="assignment.id">
            <td class="px-4 py-2">{{ assignment.probe_name || assignment.probe }}</td>
            <td class="px-4 py-2">
              <span :class="[
                'px-2 py-1 text-xs rounded-full',
                assignment.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
              ]">
                {{ assignment.is_active ? 'Yes' : 'No' }}
              </span>
            </td>
            <td class="px-4 py-2 space-x-2">
              <button
                @click="goToProbe(assignment)"
                class="bg-blue-600 text-white px-3 py-1 rounded-md hover:bg-blue-700 text-sm"
              >
                Edit
              </button>
              <button
                @click="confirmDetach(assignment)"
                class="bg-red-600 text-white px-3 py-1 rounded-md hover:bg-red-700 text-sm"
              >
                Detach
              </button>
            </td>
          </tr>
        </tbody>
      </table>

      <p v-else class="text-gray-500">No probes are currently using this profile.</p>

      <div class="mt-4">
        <button
          @click="goToAttach"
          class="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 text-sm"
        >
          Attach to Probe
        </button>
        <p class="text-xs text-gray-500 mt-2">Attach is performed from the probe detail page.</p>
      </div>
    </div>

    <div v-if="detaching" class="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50">
      <div class="bg-white/80 dark:bg-gray-800/70 backdrop-blur-md p-6 rounded-lg shadow-xl max-w-md w-full mx-4">
        <h3 class="text-lg font-semibold mb-4">Confirm Detach</h3>
        <p class="mb-4">Detach this WiFi profile from the probe?</p>
        <p class="font-medium text-gray-600 dark:text-gray-300 mb-6">
          {{ detaching.probe_name || detaching.probe }}
        </p>
        <div class="flex justify-end space-x-2">
          <button @click="detaching = null" class="px-4 py-2 bg-gray-300 text-gray-700 rounded hover:bg-gray-400">Cancel</button>
          <button @click="performDetach" class="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700">Detach</button>
        </div>
      </div>
    </div>
  </div>

  <div v-else-if="!error" class="p-6">
    <p>Loading...</p>
  </div>

  <PageError v-if="error" :error="error" :errorCode="errorCode" />
</template>

<script>
import {
  getWiFiProfileById,
  getAssignmentsByProfile,
  detachWiFiProfileFromProbe,
} from "@/services/wifiProfileService";
import PageError from "@/components/PageError.vue";
import { useStore } from "@/store";
import { formatError } from "@/utils/errorMessages";
import { useMultiTenant } from "@/composables/useMultiTenant";

export default {
  name: "WiFiProfileDetails",
  components: {
    PageError,
  },
  setup() {
    const tenant = useMultiTenant();
    return { tenant };
  },
  data() {
    return {
      profile: null,
      assignments: [],
      assignmentsLoading: false,
      revealed: false,
      detaching: null,
      canChangeWiFiProfile: false,
      error: "",
      errorCode: "",
      authTypeLabels: {
        wpa_enterprise: "WPA-Enterprise",
        wpa_psk: "WPA2-PSK",
        wpa3_sae: "WPA3-SAE",
      },
      frequencyBandLabels: {
        both: "Both (2.4 GHz + 5 GHz)",
        "2_4": "2.4 GHz only",
        "5": "5 GHz only",
      },
    };
  },
  computed: {
    user() {
      return useStore.getUser();
    },
    currentSchema() {
      return this.$route.query.schemaName || this.tenant.currentSchemaName?.value || null;
    },
    isEnterprise() {
      return this.profile?.auth_type === "wpa_enterprise";
    },
    isPsk() {
      return this.profile?.auth_type === "wpa_psk" || this.profile?.auth_type === "wpa3_sae";
    },
    authTypeLabel() {
      return this.authTypeLabels[this.profile?.auth_type] || this.profile?.auth_type || "N/A";
    },
    frequencyBandLabel() {
      return this.frequencyBandLabels[this.profile?.frequency_band] || this.profile?.frequency_band || "N/A";
    },
  },
  watch: {
    user: {
      handler(user) {
        if (user) {
          this.checkPermissions();
        }
      },
      immediate: true,
    },
  },
  async mounted() {
    await this.tenant.fetchOrganizations();
    await this.tenant.loadInitialData(() => Promise.resolve());
    await this.fetchProfile();
    await this.fetchAssignments();
  },
  methods: {
    async checkPermissions() {
      if (this.user) {
        this.canChangeWiFiProfile = await useStore.hasPermission("Can change WiFi Profile");
      }
    },
    async fetchProfile() {
      try {
        const id = this.$route.params.id;
        this.profile = await getWiFiProfileById(id, this.currentSchema);
      } catch (error) {
        console.error("Error fetching WiFi profile:", error);
        const { code, message } = formatError(error);
        this.errorCode = code;
        this.error = message;
      }
    },
    async fetchAssignments() {
      if (!this.profile) return;
      this.assignmentsLoading = true;
      try {
        const data = await getAssignmentsByProfile(this.profile.id, this.currentSchema);
        this.assignments = Array.isArray(data) ? data : [];
      } catch (error) {
        console.error("Error fetching assignments:", error);
        this.assignments = [];
      } finally {
        this.assignmentsLoading = false;
      }
    },
    async revealSecrets() {
      try {
        const id = this.$route.params.id;
        const revealedProfile = await getWiFiProfileById(id, this.currentSchema, true);
        this.profile = revealedProfile;
        this.revealed = true;
      } catch (error) {
        console.error("Error revealing secrets:", error);
        alert("Failed to reveal secrets");
      }
    },
    goToProbe(assignment) {
      this.$router.push({
        name: "ProbeDetails",
        params: { id: assignment.probe },
        query: { schemaName: this.currentSchema },
      });
    },
    goToAttach() {
      this.$router.push({ name: "Probes", query: { schemaName: this.currentSchema } });
    },
    confirmDetach(assignment) {
      this.detaching = assignment;
    },
    async performDetach() {
      if (!this.detaching) return;
      try {
        await detachWiFiProfileFromProbe(this.detaching.id, this.currentSchema);
        this.detaching = null;
        await this.fetchAssignments();
      } catch (error) {
        console.error("Error detaching:", error);
        alert("Failed to detach WiFi profile");
      }
    },
  },
};
</script>
